import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sessionActions } from './store';
import {
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  makeStyles,
  FormControl,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Input
} from '@material-ui/core';
import { Visibility, VisibilityOff, Favorite } from '@material-ui/icons';
import t from './common/localization';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F0F0F0',  // Set background of page to white
    padding: theme.spacing(2),
  },
  paper: {
    background: '#FFFFFF',  // White background for the paper
    padding: theme.spacing(4),
    maxWidth: 400,
    width: '100%',
    borderRadius: 16,
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',  // Lighter shadow to match white background
    color: '#000000',  // Black text color for readability
  },
  heading: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(3),
    fontSize: 28,
    color: '#000000',  // Black text color for the heading
    textAlign: 'center',
  },
  textField: {
    marginBottom: theme.spacing(3),
    '& label': {
      color: '#000000',  // Black color for labels
    },
    '& .MuiInputBase-root': {
      color: 'black',  // Black text in inputs
      backgroundColor: '#f0f0f0',  // Light gray background for inputs
      borderRadius: 8,
    },
    '& .MuiInputBase-root.Mui-focused': {
      backgroundColor: '#e1e1e1',  // Slightly darker gray for focused inputs
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#ff6f61',  // Red color for focused label
    }
  },
  submitButton: {
    backgroundColor: '#371DDB',  // Red button color
    color: 'white',
    padding: theme.spacing(1.5),
    borderRadius: 20,
    '&:hover': {
      backgroundColor: '#371AAB',  // Slightly lighter red when hovered
    },
  },
  footerLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
  },
  footer: {
    fontSize: '12px',
    color: '#000000',
    display: 'flex',
    alignItems: 'center',
  },
  iconLove: {
    fontSize: '12px',
    color: 'red',
    verticalAlign: 'middle',
    margin: '0 4px',
  },
  lionImgInline: {
    width: 30,
    height: 'auto',
    marginLeft: '6px',
  },
}));

const LoginPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confPassword, setConfPassword] = useState('');

  const classes = useStyles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      email: email,
      otp: otp,
      password: confPassword,
    }
    const response = await fetch("/api/users/changePasswordWithEmailOtp",{
      method: "PUT",
      headers: {
          "Content-Type": "application/json",
        },
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      const result = await response.json();
      setShowForgotPasswordDialog(false);
      setShowOtp(false);
      console.log(result);
    } else {
      const message = await response.text();
      setError(message);
    }
  };
  
  const handleGetOtp = async (e) => {
    e.preventDefault();
    const payload = {
      email: email
    }
    const response = await fetch("/api/users/getOtpForgotPassword",{
      method: "PUT",
      headers: {
          "Content-Type": "application/json",
        },
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      const result = await response.json();
      setShowForgotPasswordDialog(false);
      setShowOtp(true);
      console.log(result);
    } else {
      const message = await response.text();
      setError(message);
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    const response = await fetch('/api/session', {
      method: 'POST',
      body: new URLSearchParams(`email=${username}&password=${password}`)
    });

    if (response.ok) {
      const user = await response.json();
      dispatch(sessionActions.updateUser(user));
      history.push('/');
    } else {
      const message = await response.text();
      setErrorMessage(message);
      setFailed(true);
      setPassword('');
    }
  };

  return (
    <div className={classes.root}>
      <Paper elevation={4} className={classes.paper}>
        <Typography className={classes.heading}>
          {t('loginLogin')}
        </Typography>

        <form onSubmit={handleLogin}>

          <TextField
            variant="filled"  // No border, just a filled field
            fullWidth
            required
            label={t('username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            error={failed}
            helperText={failed ? errorMessage : ''}
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Password field styled like the username field */}
          <TextField
            variant="filled"  // No border, just a filled field
            fullWidth
            required
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={classes.submitButton}
            disabled={!username || !password}
          >
            {t('loginLogin')}
          </Button>
        </form>
        <div className={classes.footerLine}>
          <Typography className={classes.footer} onClick={(e) => { setShowForgotPasswordDialog(true) }}>
            Forgot Password?
          </Typography>
        </div>
        <div className={classes.footerLine}>
          <Typography className={classes.footer}>
            Made with <Favorite className={classes.iconLove} /> in INDIA
          </Typography>
          <img src="images/lion.jpg" alt="Make in India" className={classes.lionImgInline} />
        </div>
      </Paper>

      <Dialog
        open={showForgotPasswordDialog}
        onClose={() => setShowForgotPasswordDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          Forgot Password?
        </DialogTitle>

        <DialogContent>
          <form autoComplete="off" noValidate>
            <TextField
              fullWidth
              required
              label="Email"
              placeholder="Enter Registered Email ID"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              error={failed}
              helperText={failed ? errorMessage : ''}
              margin="dense"
              variant='filled'
            />

            {error && (
              <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>
            )}
          </form>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowForgotPasswordDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleGetOtp} 
            variant="contained" 
            color="primary" 
            autoFocus
          >
            Get OTP
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showOtp}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            Enter OTP
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="otp"
              label="OTP"
              onChange={(e)=>{setOtp(e.target.value)}}
              value={otp}
              type="text"
              fullWidth
              variant="filled"
            />
            <TextField
              margin="dense"
              id="newPassword"
              label="New Password"
              onChange={(e)=>{setNewPassword(e.target.value)}}
              value={newPassword}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="filled"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="dense"
              id="confirmPassword"
              label="Confirm Password"
              onChange={(e)=>{setConfPassword(e.target.value)}}
              value={confPassword}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="filled"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <p style={{ color: 'red' }}>{error}</p>}
          </DialogContent>
                      
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="secondary" onClick={()=>{setShowOtp(false)}}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={otp.length !== 6}>
              Submit
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
